import { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { Input } from "antd5";

import Button from "components/ui/button/Button";
import Drawer from "components/ui/drawer/Drawer";
import { TrackingProvider } from "lib/tracking";
import { useDebouncedValue } from "../../../lib/debounce";
import { SearchLeadsRequest } from "../../../lib/generated/app-service-gql/graphql";
import { useInfiniteLeads } from "../../../lib/hooks/api/leads/useLeads";
import UIcon from "../../../lib/icons/untitled_ui/UIcon";
import { LeadSortBy, LeadSortOrder } from "../../../lib/types/graphQLEnums";
import { pluralise } from "../../../lib/utils";
import { Flex, Text } from "../../../styles/utility-components";
import { AllEntities } from "../../entities/utils";
import { VIEW_LEAD_LIST_CONTAINER_ID, ViewLeadsList } from "./ViewLeadsList";

type Props = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  savedLeadIds: string[];
  entityType: AllEntities;
};

/**
 * Slideout drawer which opens and allows us to view all of the leads an entity is saved to
 */
export default function ViewLeadsDrawer({ isOpen, onOpenChange, entityType, savedLeadIds }: Props) {
  const [filters, setFilters] = useState<SearchLeadsRequest>({
    orderBy: LeadSortBy.Name,
    order: LeadSortOrder.Asc,
    leadIds: savedLeadIds,
  });

  // Reset filters when savedLeadIds changes (drawer reopened for different entity)
  useEffect(() => {
    setFilters({
      orderBy: LeadSortBy.Name,
      order: LeadSortOrder.Asc,
      leadIds: savedLeadIds,
      searchText: undefined,
    });
  }, [savedLeadIds]);

  const [debouncedFilters] = useDebouncedValue(filters, 500);
  const { data, hasNextPage, fetchNextPage, isLoading, isError } = useInfiniteLeads(
    debouncedFilters,
    20,
  );

  const allLeads = data?.pages.flatMap((page) => page.leads.results) ?? [];
  return (
    <Drawer open={isOpen} onOpenChange={onOpenChange} width={486} padding={24}>
      <TrackingProvider data={{ "Data type": entityType.toUpperCase() }}>
        <DrawerContent aria-label={`View ${entityType} leads drawer`}>
          <Flex
            justifyContent="space-between"
            alignItems="flex-start"
            aria-label="View leads drawer header"
          >
            <Text h2>Saved to {pluralise(savedLeadIds.length, "lead")}</Text>
            <Button
              as="button"
              buttonType="secondary"
              icon="xClose"
              onClick={() => onOpenChange(false)}
            />
          </Flex>

          <Flex column height="100%">
            {/* Only show search when total leads number more than 10 (unlikely)*/}
            {allLeads.length > 10 && (
              <Input
                placeholder="Search"
                prefix={<UIcon icon="searchRefraction" size={16} />}
                value={filters.searchText ?? ""}
                onChange={(e) => {
                  setFilters({ ...filters, searchText: e.target.value });
                }}
                allowClear
                style={{ marginBottom: 24 }}
              />
            )}
            <ListContainer
              id={VIEW_LEAD_LIST_CONTAINER_ID}
              aria-label={VIEW_LEAD_LIST_CONTAINER_ID}
            >
              <ViewLeadsList
                isLoading={isLoading}
                isError={isError}
                leads={allLeads}
                hasNextPage={hasNextPage || false}
                fetchNextPage={fetchNextPage}
              />
            </ListContainer>
          </Flex>
        </DrawerContent>
      </TrackingProvider>
    </Drawer>
  );
}

const DrawerContent = styled(Flex)({
  flexDirection: "column",
  height: "100%",
  gap: 24,
  maxWidth: "440px",
});

const ListContainer = styled(Flex)({
  flexDirection: "column",
  flexGrow: 1,
  height: "100%",
  overflowY: "auto",
});
